import { z } from 'zod';
import { idSchema } from './common.js';

/** Categoría fija por estado: la lógica común (completado, cancelado) no depende del nombre. */
export const STATE_CATEGORIES = ['backlog', 'todo', 'in_progress', 'done', 'canceled'] as const;
export type StateCategory = (typeof STATE_CATEGORIES)[number];
export const stateCategorySchema = z.enum(STATE_CATEGORIES);

export type StateSeed = {
  name: string;
  category: StateCategory;
  color: string;
  isDefault: boolean;
};

/**
 * Un proyecto recién creado ya sirve para trabajar: sin esto la primera tarea no tiene dónde
 * caer. Vive en contracts porque la crea la API y la reproduce el seed de desarrollo.
 */
export const DEFAULT_STATES: readonly StateSeed[] = [
  { name: 'Backlog', category: 'backlog', color: '#94a3b8', isDefault: false },
  { name: 'To do', category: 'todo', color: '#3b82f6', isDefault: true },
  { name: 'In progress', category: 'in_progress', color: '#f59e0b', isDefault: false },
  { name: 'Done', category: 'done', color: '#22c55e', isDefault: false },
  { name: 'Canceled', category: 'canceled', color: '#6b7280', isDefault: false },
];

/** The board a Vitto Space gets when it has none: the admission seeds it and the admin's «Create board» makes it — ONE constant, so they cannot drift. */
export const DEFAULT_BOARD = { key: 'TASK', name: 'Tasks' } as const;

/** Clave de proyecto: mayúsculas, es el prefijo del identificador público `VIT-123`. */
export const PROJECT_KEY_PATTERN = /^[A-Z][A-Z0-9]{1,7}$/;
export const projectKeySchema = z
  .string()
  .transform((value) => value.toUpperCase())
  .pipe(z.string().regex(PROJECT_KEY_PATTERN, 'invalid key'));

const colorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'invalid hexadecimal color');

export const projectSchema = z.object({
  id: idSchema,
  key: z.string(),
  name: z.string(),
  descriptionMd: z.string().nullable(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type ProjectView = z.infer<typeof projectSchema>;

export const createProjectSchema = z
  .object({
    id: idSchema.optional(),
    key: projectKeySchema,
    name: z.string().min(1).max(200),
    descriptionMd: z.string().max(64_000).nullable().optional(),
  })
  .strict();
export type CreateProjectInput = z.infer<typeof createProjectSchema>;

/** `key` no se puede parchear: es parte de identificadores ya compartidos (diseño §7). */
export const projectPatchSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    descriptionMd: z.string().max(64_000).nullable().optional(),
  })
  .strict();
export type ProjectPatchInput = z.infer<typeof projectPatchSchema>;

export const stateSchema = z.object({
  id: idSchema,
  name: z.string(),
  category: stateCategorySchema,
  position: z.number().int(),
  color: z.string(),
  isDefault: z.boolean(),
  /** Aviso del equipo a sí mismo: nunca bloquea (diseño K2, paridad con la classic). */
  wipLimit: z.number().int().positive().nullable(),
});
export type StateView = z.infer<typeof stateSchema>;

/**
 * El mismo estado, diciendo de qué proyecto es. Las vistas que mezclan proyectos (`/my`, la
 * búsqueda) no pueden pedir los estados proyecto a proyecto: sin el catálogo entero en una
 * petición la columna Estado no sabe pintar el actual ni ofrecer los demás, y su filtro se
 * queda sin valores.
 */
export const workspaceStateSchema = stateSchema.extend({
  projectId: idSchema,
  projectKey: z.string(),
});
export type WorkspaceStateView = z.infer<typeof workspaceStateSchema>;

const stateInputSchema = z
  .object({
    id: idSchema.optional(),
    name: z.string().min(1).max(64),
    category: stateCategorySchema,
    color: colorSchema.default('#94a3b8'),
    isDefault: z.boolean().default(false),
    /** Sin límite por defecto: quien no sabe de esto no lo pone a cero por accidente. */
    wipLimit: z.number().int().positive().nullable().default(null),
  })
  .strict();

/**
 * Upsert por `id` más retirada explícita: quitar un estado con items exige decir a dónde van,
 * porque ni borrar los items ni dejarlos colgados es aceptable (diseño §7).
 */
export const putStatesSchema = z
  .object({
    states: z.array(stateInputSchema).min(1).max(20),
    remove: z
      .array(z.object({ id: idSchema, migrateTo: idSchema }).strict())
      .max(20)
      .default([]),
  })
  .strict();
export type PutStatesInput = z.infer<typeof putStatesSchema>;
